/**
 * Lets host pages drive Koko's bird to perform UI actions visibly:
 * fly to an element, "type" into an input character-by-character,
 * click a button, then return to perch. Used by the studio bridge
 * so tool_use calls (e.g. `add_character`) play out as a watchable
 * choreography instead of a silent state mutation.
 *
 * The agent is intentionally low-level (one operation at a time).
 * Higher-level orchestration (queueing multiple choreographies,
 * cancelling on user interrupt) lives in the host.
 */

import type { MovementController } from "../sprite/MovementController";
import type { SpriteRenderer } from "../sprite/SpriteRenderer";

/**
 * Per-character delay while typing into an input. Fast enough that
 * a 60-char description completes in ~1s, slow enough that the user
 * sees the text materialize letter by letter.
 */
const DEFAULT_TYPE_CHAR_DELAY_MS = 18;
/** Random jitter (± half this) added to each char delay for an
 *  organic feel — nothing types at a perfect cadence. */
const TYPE_CHAR_DELAY_JITTER_MS = 14;
/**
 * Brief pause after the bird arrives at a target before it starts
 * typing/clicking. Reads as "looking at the thing" rather than a
 * machine that snaps from one action to the next.
 */
const ARRIVAL_DWELL_MS = 180;
/** How long the bird visibly "presses" a button before the click event fires. */
const CLICK_PRESS_MS = 140;

/** Interface for an element-or-coords flight target. */
type FlightTarget = HTMLElement | { viewportX: number; viewportY: number };

export interface KokoActionAgent {
  /** Fly the bird to a DOM element or fixed viewport coordinates. */
  flyTo(target: FlightTarget): Promise<void>;
  /**
   * "Type" into a text input, textarea, or contenteditable element.
   * Sets the value/textContent character-by-character with realistic
   * delays + dispatches `input` events so any listeners (like form
   * controllers) react. Ends with focus blur so blur-handlers commit.
   */
  typeInto(
    target: HTMLInputElement | HTMLTextAreaElement | HTMLElement,
    text: string,
    options?: { charDelayMs?: number },
  ): Promise<void>;
  /**
   * "Press" a button — visible animation hint then call `.click()`.
   * Caller is responsible for flying the bird to the button first.
   */
  click(button: HTMLElement): Promise<void>;
  /**
   * Wait until the next browser paint has settled. Useful right after
   * a panel switch so freshly-mounted elements are queryable.
   */
  waitForPaint(): Promise<void>;
  /**
   * Return the bird to its normal cursor-follow behavior. Call at the
   * end of a choreography sequence.
   */
  perch(): void;
  /**
   * Cancel anything in flight (typing loop, etc). Used when the user
   * starts a new turn so the bird doesn't keep typing through an
   * out-of-date plan.
   */
  cancel(): void;
  /**
   * True while the agent is mid-choreography. The host queue uses
   * this to know whether to suppress pointing/[OPEN:...] flights
   * while the bird is busy typing.
   */
  isBusy(): boolean;
  /**
   * Mark the start/end of a choreography. The host queue calls these
   * around its async work so `isBusy()` reflects the queue state, not
   * just whether a single primitive is running.
   */
  beginChoreography(): void;
  endChoreography(): void;
}

interface KokoActionAgentOptions {
  movementController: MovementController;
  spriteRenderer: SpriteRenderer;
  /**
   * Optional hook called whenever a choreography starts/ends. Lets
   * the host (e.g. KokoApp) suspend its proactive observer or other
   * idle-driven behaviors while the bird is performing an action.
   */
  onBusyChange?: (isBusy: boolean) => void;
}

export function createKokoActionAgent(
  options: KokoActionAgentOptions,
): KokoActionAgent {
  const movement = options.movementController;
  const sprite = options.spriteRenderer;
  let cancelToken = 0;
  let activeChoreographyDepth = 0;

  /**
   * The element the bird is currently pointing at, ringed with
   * `.koko-action-target` so the user can SEE the exact control — a hard
   * requirement for pointing precision. Only one element is highlighted
   * at a time; flying to a new target moves the ring, perch/cancel clear it.
   */
  const TARGET_CLASS = "koko-action-target";
  let highlightedTarget: HTMLElement | null = null;

  const highlight = (element: HTMLElement | null): void => {
    if (highlightedTarget === element) return;
    if (highlightedTarget) highlightedTarget.classList.remove(TARGET_CLASS);
    highlightedTarget = element;
    if (highlightedTarget) highlightedTarget.classList.add(TARGET_CLASS);
  };

  const clearHighlight = (): void => highlight(null);

  const setBusy = (busy: boolean): void => {
    options.onBusyChange?.(busy);
  };

  const flyTo: KokoActionAgent["flyTo"] = async (target) => {
    const coords = resolveFlightCoords(target);
    if (!coords) return;
    // Ring the destination control so the user can see precisely where the
    // bird is headed before it acts (coords-only targets carry no element).
    highlight(target instanceof HTMLElement ? target : null);
    sprite.setAnimation("swoop");
    await movement.flyToPointingTarget(coords);
    // `flyToPointingTarget` ends in pointing pose; let the bird hover
    // briefly so the eye registers arrival before the next action.
    await waitMs(ARRIVAL_DWELL_MS);
  };

  const typeInto: KokoActionAgent["typeInto"] = async (
    target,
    rawText,
    typeOptions,
  ) => {
    // Sanitize before typing. Anthropic responses with web_search
    // sometimes include `<cite index="...">…</cite>` markup around
    // quoted material — when those land in a form input verbatim
    // they read as raw HTML to the user. Strip the tags but keep
    // the inner text since that's the actual content Koko meant.
    const text = sanitizeForTyping(rawText);
    const charDelay = typeOptions?.charDelayMs ?? DEFAULT_TYPE_CHAR_DELAY_MS;
    sprite.setAnimation("pointing");
    target.focus();

    const isPlainTextField =
      target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

    // Reset to empty so we type from scratch — even if a previous
    // run left content. The user-visible animation is incremental.
    if (isPlainTextField) {
      target.value = "";
      target.dispatchEvent(new Event("input", { bubbles: true }));
    } else if (target.isContentEditable) {
      target.textContent = "";
      target.dispatchEvent(new Event("input", { bubbles: true }));
    }

    const initialCancelToken = cancelToken;
    for (let charIndex = 0; charIndex < text.length; charIndex++) {
      if (cancelToken !== initialCancelToken) break;
      const sliced = text.slice(0, charIndex + 1);
      if (isPlainTextField) {
        target.value = sliced;
      } else if (target.isContentEditable) {
        target.textContent = sliced;
      }
      target.dispatchEvent(new Event("input", { bubbles: true }));

      // ± half-jitter range each tick for an organic cadence.
      const jitter = (Math.random() - 0.5) * TYPE_CHAR_DELAY_JITTER_MS;
      await waitMs(Math.max(4, charDelay + jitter));
    }

    if (cancelToken === initialCancelToken) {
      // Fire change + blur so any commit-on-blur listeners run.
      target.dispatchEvent(new Event("change", { bubbles: true }));
      target.blur();
    }
  };

  const click: KokoActionAgent["click"] = async (button) => {
    sprite.setAnimation("pointing");
    // Visual press: briefly add a class so CSS can style the press,
    // then trigger the actual click. The button's onclick handler
    // runs synchronously inside `.click()`.
    button.classList.add("is-koko-pressing");
    await waitMs(CLICK_PRESS_MS);
    button.classList.remove("is-koko-pressing");
    button.click();
  };

  const waitForPaint: KokoActionAgent["waitForPaint"] = () => {
    return new Promise<void>((resolve) => {
      // Two RAFs is the standard "next paint settled" dance — one
      // for layout, one for the actual paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  };

  const perch: KokoActionAgent["perch"] = () => {
    clearHighlight();
    movement.returnToFollow();
  };

  const cancel: KokoActionAgent["cancel"] = () => {
    cancelToken++;
    clearHighlight();
  };

  const isBusy: KokoActionAgent["isBusy"] = () => activeChoreographyDepth > 0;

  const beginChoreography: KokoActionAgent["beginChoreography"] = () => {
    activeChoreographyDepth++;
    if (activeChoreographyDepth === 1) setBusy(true);
  };

  const endChoreography: KokoActionAgent["endChoreography"] = () => {
    activeChoreographyDepth = Math.max(0, activeChoreographyDepth - 1);
    if (activeChoreographyDepth === 0) setBusy(false);
  };

  return {
    flyTo,
    typeInto,
    click,
    waitForPaint,
    perch,
    cancel,
    isBusy,
    beginChoreography,
    endChoreography,
  };
}

function resolveFlightCoords(
  target: FlightTarget,
): { viewportX: number; viewportY: number } | null {
  if (target instanceof HTMLElement) {
    // Scroll the element into view if needed, then read its center.
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    const rect = target.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return null;
    return {
      viewportX: rect.left + rect.width / 2,
      viewportY: rect.top + rect.height / 2,
    };
  }
  return target;
}

function waitMs(durationMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    window.setTimeout(() => resolve(), durationMs);
  });
}

/**
 * Strip out artifacts that should never appear in a typed-out
 * string. Right now the only known offender is Anthropic's
 * `<cite index="…">…</cite>` markup that ships in web-search
 * responses — typed verbatim it leaks raw HTML into form fields.
 * We keep the inner text (it's the actual content Koko meant) and
 * drop the wrapper tags.
 */
function sanitizeForTyping(rawText: string): string {
  return rawText
    .replace(/<cite\b[^>]*>([\s\S]*?)<\/cite>/gi, "$1")
    .replace(/<\/?cite\b[^>]*>/gi, "")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}
